import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useStateContext } from "contexts/ContextProvider";
import {
  Box,
  useTheme,
  Typography,
  Divider,
  useMediaQuery,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  TextField,
  Stack,
  Paper,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";

import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import linguistApi from "services/linguistApi";
import Tabs from "@mui/material/Tabs";

import { a11yProps, CustomTabPanel } from "components/CustomTabPanel";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";

import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";

import {
  formatAmount,
  formatDurationToHHMM,
  formatTimeToHHMM12Hr,
  formatDateLong,
  getStatusColor,
} from "helpers/ServiceHelpers";
import { Card } from "components/Card";
import { Card2 } from "components/Card2";

import CustomTab from "components/CustomTab";

import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { useWebSocket } from "contexts/WebSocketContext";
import { formatServiceCommentTime } from "scenes/services/interpretations/InterpretationProfile";
import { OVERLAY_NAMES } from "constants/Overlays";

const formatCurrencyValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "$0";
  }

  return `$${value}`;
};

// Keep only fields this component needs so we don't retain member-related data
const sanitizeInterpretationData = (data = {}) => {
  if (!data || typeof data !== "object") return {};

  const allowedKeys = [
    "id",
    "Linguist",
    "ServiceTargetLanguages",
    "ProjectNumber",
    "Rate",
    "InterpretationStatus",
    "ContactPersons",
    "ContactPersonName",
    "ContactPersonNumber",
    "ContactPersonExt",
    "ContactPerson",
    "Name",
    "ServiceSubtype",
    "Address",
    "Details",
    "SpecialInstructions",
    "SpecialInstruction",
    "Instructions",
    "TimeStart",
    "TimeEnd",
    "Duration",
    "ActualStart",
    "ActualEnd",
    "ActualDuration",
    "SchedulingTime",
    "PreparationTime",
    "TotalDuration",
    "CancellationReason",
    "Date",
    "TotalLinguistPay",
    "CostLines",
    "Comments",
    "Notes",
  ];

  const sanitized = {};
  allowedKeys.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(data, k)) {
      sanitized[k] = data[k];
    }
  });

  // If ContactPersons is not an array but legacy fields exist, compose a contact object
  if (!Array.isArray(sanitized.ContactPersons)) {
    sanitized.ContactPersons = [];
    if (
      data.ContactPersonName ||
      data.ContactPersonNumber ||
      data.ContactPerson?.Email
    ) {
      sanitized.ContactPersons.push({
        Name: data.ContactPersonName || data.ContactPerson?.Name || "",
        PhoneNumber:
          data.ContactPersonNumber || data.ContactPerson?.Number || "",
        Ext: data.ContactPersonExt || data.ContactPerson?.Ext || "",
        Email: data.ContactPerson?.Email || "",
      });
    }
  }

  return sanitized;
};

const LinguistAssignmentDetail = () => {
  const SERVICE_PROFILE_VIEW_KEY = "SERVICE_PROFILE_VIEW_MODE";
  const [pageTitle, setPageTitle] = useState("Interpretation");
  usePageTitle(pageTitle);
  const canUseModernDetailsView = false;
  const { socket } = useWebSocket();
  const [tabValue, setTabValue] = React.useState(0);
  const [mobileTabValue, setMobileTabValue] = React.useState(0);
  const [useModernDetailsView] = useState(() => {
    try {
      if (!canUseModernDetailsView) return false;
      return localStorage.getItem(SERVICE_PROFILE_VIEW_KEY) === "modern";
    } catch (error) {
      console.error("Failed to read service profile view mode:", error);
      return false;
    }
  });
  const isModernDetailsEnabled =
    canUseModernDetailsView && useModernDetailsView;
  const showMobileOverview = !isModernDetailsEnabled;

  useEffect(() => {
    if (!canUseModernDetailsView) return;
    localStorage.setItem(
      SERVICE_PROFILE_VIEW_KEY,
      useModernDetailsView ? "modern" : "classic",
    );
  }, [canUseModernDetailsView, useModernDetailsView]);

  // Refs for mobile scroll-to-view and scroll spy
  const detailsRef = useRef(null);
  const commentsRef = useRef(null);
  const actionsRef = useRef(null);
  const mobileScrollRef = useRef(null);
  const mobileTabRef = useRef(0);

  const mobileSectionRefs = useMemo(
    () => [detailsRef, commentsRef, actionsRef],
    [],
  );

  const handleTabsChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMobileTabsChange = (event, newValue) => {
    setMobileTabValue(newValue);
    mobileTabRef.current = newValue;
    const sectionRef = mobileSectionRefs[newValue];
    if (sectionRef?.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const maxTabIndex = Math.max(0, mobileSectionRefs.length - 1);
    if (mobileTabRef.current > maxTabIndex) {
      mobileTabRef.current = maxTabIndex;
      setMobileTabValue(maxTabIndex);
    }
  }, [mobileSectionRefs]);

  const { id } = useParams();
  const location = useLocation();
  const theme = useTheme();

  const isMobile = !useMediaQuery("(min-width:600px)");
  // Scroll spy effect to update active tab based on scroll position
  useEffect(() => {
    if (!isMobile || !mobileScrollRef.current) return;

    const observerOptions = {
      root: mobileScrollRef.current,
      rootMargin: "-88px 0px -60% 0px",
      threshold: [0.1, 0.25, 0.5, 0.75],
    };

    const observerCallback = (entries) => {
      let mostVisible = null;
      let maxRatio = 0;

      entries.forEach((entry) => {
        if (entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          mostVisible = entry.target;
        }
      });

      if (mostVisible) {
        const index = parseInt(mostVisible.getAttribute("data-index"));
        const tabIndex = Math.max(index, 0);
        if (!isNaN(index) && tabIndex !== mobileTabRef.current) {
          mobileTabRef.current = tabIndex;
          setMobileTabValue(tabIndex);
        }
      }
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    mobileSectionRefs.forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [isMobile, mobileSectionRefs]);

  const {
    refreshPage,
    triggerPageRefresh,
    showSnackbar,
    setOverlayName,
    setOverlayData,
  } = useStateContext();

  const [serviceCostSummary, setServiceCostSummary] = useState(null);

  const [interpretationData, setInterpretationData] = useState({});
  const [listOfStatuses, setListOfStatuses] = useState([]);

  // Set entity tracking for presence and updates
  const { setCurrentEntity } = usePresence();
  usePagePresence(); // Join entity room when entity is set

  useEffect(() => {
    if (id) {
      const entityId = parseInt(id);

      setCurrentEntity({
        entityType: "Interpretation",
        entityId,
      });
    }
    return () => {
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [id, setCurrentEntity]);

  // Listen for assignment updates from other members
  const fetchInterpretationData = useCallback(() => {
    if (!id) return Promise.resolve();
    // Call only the linguist portal endpoint — do not fallback to admin/member endpoints
    return linguistApi
      .get(`/services/${id}`)
      .then((response) => {
        const sanitized = sanitizeInterpretationData(response.data);
        setInterpretationData(sanitized);

        try {
          const title =
            sanitized.Name || sanitized.ProjectNumber || `Interpretation ${id}`;
          setPageTitle(title);
        } catch (e) {}
        return sanitized;
      })
      .catch((err) => {
        console.log("Error fetching assignment:", err);
        const serverMessage =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load assignment";
        try {
          showSnackbar(
            `Failed to load assignment: ${serverMessage}`,
            theme,
            "error",
          );
        } catch (e) {}
        return null;
      });
  }, [id, setPageTitle, showSnackbar, theme]);

  const getDataLists = useCallback(() => {
    Promise.all([linguistApi.get("/services/interpretations/statuses")])
      .then(([statusesResponse]) => {
        if (statusesResponse) {
          setListOfStatuses(statusesResponse.data);
        }
      })
      .catch((error) => {
        console.log("error:", error);
      });
  }, [id, theme]);

  // Comments state (local view)
  const [comments, setComments] = useState([]);
  const commentsListRef = useRef(null);
  const [newCommentText, setNewCommentText] = useState("");

  // Menu state variables
  const [callMenuAnchor, setCallMenuAnchor] = useState(null);
  const [callMenuOpen, setCallMenuOpen] = useState(false);
  const [addressMenuAnchor, setAddressMenuAnchor] = useState(null);
  const [addressMenuOpen, setAddressMenuOpen] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!id) return;
    try {
      const resp = await linguistApi.get(`/services/${id}/comments`);
      const raw = Array.isArray(resp?.data) ? resp.data : [];

      const normalized = raw.map((c, index) => {
        // comments from server use fields: Comment, Member, Linguist, CreatedAt, EditedAt, EditHistory
        const history = Array.isArray(c.EditHistory) ? c.EditHistory : [];
        const createdOn = c.CreatedAt || c.createdAt || null;
        const updatedOn = c.EditedAt || c.editedAt || null;

        return {
          id: c.id || `service-comment-${index}`,
          note: String(c.Comment || c.CommentText || "").trim(),
          author: c.Member?.Name || c.Linguist?.Name || c.MemberName || "N/A",
          authorType: c.Linguist ? "linguist" : c.Member ? "member" : "unknown",
          createdOn,
          createdLabel: formatServiceCommentTime(createdOn),
          editedLabel: formatServiceCommentTime(updatedOn),
          hasEditHistory: history.length > 0 || Boolean(updatedOn),
          historyEntries: (history || []).map((entry, historyIndex) => ({
            id: `${c.id || index}-${historyIndex}`,
            label: "Edited",
            changedOn: entry?.editedAt || entry?.editedOn || null,
            changedBy: null,
            noteFrom: entry?.previousComment || entry?.previous || null,
            noteTo: null,
          })),
        };
      });

      normalized.sort(
        (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      );
      setComments(normalized);
    } catch (err) {
      console.warn("Failed to load comments", err);
    }
  }, [id]);

  // Auto-scroll comments to bottom when new comments arrive
  useEffect(() => {
    if (!commentsListRef?.current) return;
    try {
      requestAnimationFrame(() => {
        const el = commentsListRef.current;
        el.scrollTop = el.scrollHeight;
      });
    } catch (e) {
      const el = commentsListRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [comments.length]);

  // Also scroll when Comments tab is opened (desktop or mobile)
  useEffect(() => {
    const commentsDesktopIndex = 1; // in this component Comments is tab index 1 for desktop
    const commentsMobileIndex = 1; // mobile tabs: 0=Details,1=Comments

    const isCommentsActive =
      (!isMobile && tabValue === commentsDesktopIndex) ||
      (isMobile && mobileTabValue === commentsMobileIndex);
    if (!isCommentsActive) return;
    if (!commentsListRef?.current) return;

    try {
      requestAnimationFrame(() => {
        const el = commentsListRef.current;
        el.scrollTop = el.scrollHeight;
      });
    } catch (e) {
      const el = commentsListRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [tabValue, mobileTabValue, isMobile]);

  const addComment = async () => {
    const trimmed = String(newCommentText || "").trim();
    if (!trimmed) return;
    const optimistic = {
      text: trimmed,
      createdAt: new Date().toISOString(),
      authorType: "linguist",
      authorName: interpretationData?.Linguist?.Name || "You",
    };
    setComments((s) => [...s, optimistic]);
    setNewCommentText("");
    try {
      if (id) {
        await linguistApi.post(`/services/${id}/comments`, {
          comment: trimmed,
        });
        await fetchComments();
        showSnackbar("Comment added.", theme, "success");
      }
    } catch (err) {
      console.error("Add comment failed", err);
      showSnackbar("Failed to post comment — saved locally.", theme, "warning");
    }
  };

  const canFinalizeAssignment = useCallback(() => {
    if (!interpretationData.Date || !interpretationData.TimeEnd) {
      return false;
    }

    try {
      // Parse the assignment date using dayjs
      const assignmentDate = dayjs(interpretationData.Date);
      const endTimeString = interpretationData.TimeEnd;

      // Parse the end time (assuming format like "HH:mm" or "HH:mm:ss")
      const [hours, minutes] = endTimeString.split(":").map(Number);

      // Set the end time on the assignment date (EST timezone)
      const assignmentEndTime = assignmentDate
        .hour(hours)
        .minute(minutes)
        .second(0)
        .millisecond(0);

      // Get current time in user's local timezone
      const now = dayjs();

      // Convert assignment end time to user's local timezone for comparison
      // Assignment is stored in EST (UTC-5), so we need to adjust for the user's timezone offset
      const userTimezoneOffset = new Date().getTimezoneOffset(); // in minutes (negative for UTC+)
      const estOffset = 300; // EST is UTC-5 = 300 minutes behind UTC

      // Calculate the total offset difference between user's timezone and EST
      const totalOffset = userTimezoneOffset + estOffset;

      // Convert assignment end time to user's local timezone
      const assignmentEndTimeInUserTimezone = assignmentEndTime.subtract(
        totalOffset,
        "minutes",
      );

      // Check if current local time is after the assignment end time (converted to user's timezone)
      return now.isAfter(assignmentEndTimeInUserTimezone);
    } catch (error) {
      console.error("Error checking finalize eligibility:", error);
      return false;
    }
  }, [interpretationData.Date, interpretationData.TimeEnd]);

  const finalizeAssignment = async () => {
    if (!id) return;

    const secondData = {
      ...interpretationData,
      id: id,
      InterpretationStatuses: listOfStatuses,
    };

    setOverlayData(secondData);
    setOverlayName(OVERLAY_NAMES.LINGUIST_ASSIGNMENT_FINALIZE);
  };

  const handleAssignmentRefetch = useCallback(
    async (updatedBy, meta = {}) => {
      // Attempt to reload the assignment data (sanitized) so member data is not retained
      try {
        const sanitized = await fetchInterpretationData();

        // Always set state (merge) to ensure a state update even if some fields didn't change
        if (sanitized) {
          setInterpretationData((prev) => ({
            ...(prev || {}),
            ...sanitized,
            __refetchedAt: Date.now(),
          }));
        }
      } catch (err) {
        // fetchInterpretationData handles its own errors, but ensure we don't break the update flow
        console.warn("handleAssignmentRefetch: fetch failed", err);
      }

      const changeType = meta?.changeType || "Assignment";
      const message = `${changeType} updated.`;
      triggerPageRefresh(message, theme);

      const normalizedChangeType = String(meta?.changeType || "").toLowerCase();
      const normalizedChangeAction = String(
        meta?.changeAction || "",
      ).toLowerCase();
      if (
        normalizedChangeType.includes("comment") ||
        normalizedChangeAction.includes("comment")
      ) {
        fetchComments().catch((err) => {
          console.warn(
            "Failed to refresh comments after assignment update",
            err,
          );
        });
      }
    },
    [fetchComments, fetchInterpretationData, triggerPageRefresh, theme],
  );

  useAssignmentUpdates({
    modelType: "Interpretation",
    recordId: id ? parseInt(id) : null,
    onUpdate: handleAssignmentRefetch,
  });

  // Check if linguist has access to this service before loading
  const checkLinguistAccess = useCallback(async () => {
    if (!id) return false;
    try {
      const response = await linguistApi.get(`/services/${id}/can-access`);
      return response.data?.canAccess === true;
    } catch (error) {
      console.log("Linguist access check failed:", error);
      return false;
    }
  }, [id]);

  // Initial load (and reload when id changes)
  useEffect(() => {
    if (id) {
      // Check access before loading
      checkLinguistAccess().then((hasAccess) => {
        if (!hasAccess) {
          console.warn("Linguist does not have access to this assignment");
          showSnackbar(
            "You do not have access to this assignment.",
            theme,
            "error",
          );
          // Redirect back to the previous page or linguist dashboard
          setTimeout(() => {
            navigate("/linguist-portal", { replace: true });
          }, 1500);
          return;
        }
        fetchInterpretationData();
        getDataLists();
        fetchComments();
      });
      return;
    }

    // If no id in the URL, try to use location.state as a fallback (sanitized)
    try {
      const statePayload = location?.state;
      const possible =
        statePayload?.record ||
        statePayload?.assignment ||
        statePayload?.service ||
        statePayload?.interpretation ||
        statePayload;

      if (possible && typeof possible === "object") {
        const sanitized = sanitizeInterpretationData(possible);
        // only set if it contains something meaningful
        if (
          sanitized &&
          (sanitized.ProjectNumber || sanitized.Name || sanitized.Linguist)
        ) {
          setInterpretationData(sanitized);
          try {
            const title =
              sanitized.Name || sanitized.ProjectNumber || "Interpretation";
            setPageTitle(title);
          } catch (e) {}
        }
      }
    } catch (e) {
      /* ignore */
    }
  }, [id, fetchInterpretationData]);

  const navigate = useNavigate();

  const targetLanguage =
    interpretationData.ServiceTargetLanguages &&
    interpretationData.ServiceTargetLanguages.length > 0
      ? interpretationData.ServiceTargetLanguages[0]?.Language?.Name
      : "N/A";

  const linguistName = interpretationData.Linguist?.Name?.trim() || "";
  const normalizedLinguistName = linguistName.toLowerCase();
  const isLinguistUnavailable =
    !linguistName ||
    normalizedLinguistName === "n/a" ||
    normalizedLinguistName === "na" ||
    normalizedLinguistName === "none" ||
    normalizedLinguistName === "null" ||
    normalizedLinguistName === "undefined" ||
    normalizedLinguistName === "-";

  // Display status for rendering - show just "Cancelled" if status contains "Cancelled:"
  const displayStatus = (() => {
    const status = interpretationData.InterpretationStatus || "";
    if (status.startsWith("Cancelled")) {
      return "Cancelled";
    }
    return status;
  })();

  const hasMeaningfulValue = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    return Boolean(
      normalized &&
      !["n/a", "na", "none", "null", "undefined", "-"].includes(normalized),
    );
  };

  // Prepare cost lines to display (non-zero, meaningful)
  const allCostLines = Array.isArray(interpretationData.CostLines)
    ? interpretationData.CostLines
    : [];

  const visibleCostLines = allCostLines.filter((cl) => {
    const amt = Number(cl?.Amount ?? cl?.amount ?? 0);
    if (!Number.isFinite(amt)) return false;
    return Math.abs(amt) > 0.005; // treat near-zero as zero
  });

  // Compute a reliable total to display: prefer serviceCostSummary, then interpretation field, then sum of cost lines
  const totalFromSummary =
    serviceCostSummary?.componentBreakdown?.TotalLinguistPay;
  const interpretationTotalRaw = interpretationData.TotalLinguistPay;
  const interpretationTotalNum = Number(interpretationTotalRaw);
  const sumOfVisible = visibleCostLines.reduce(
    (sum, c) => sum + (Number(c?.Amount ?? c?.amount ?? 0) || 0),
    0,
  );
  const computedTotal =
    totalFromSummary !== undefined && totalFromSummary !== null
      ? Number(totalFromSummary)
      : Number.isFinite(interpretationTotalNum) && interpretationTotalRaw !== ""
        ? interpretationTotalNum
        : sumOfVisible;

  const buildCallLink = (phoneNumber, ext) => {
    const phone = String(phoneNumber || "").trim();
    const extension = String(ext || "").trim();

    if (!hasMeaningfulValue(phone)) return "";
    if (!hasMeaningfulValue(extension)) return `tel:${phone}`;

    // Pause-based extension format for better dialer compatibility.
    return `tel:${phone},,${extension}`;
  };

  const openCallLink = (phoneNumber, ext) => {
    const callLink = buildCallLink(phoneNumber, ext);
    if (!callLink) return;

    window.open(callLink, "_blank");
  };

  const contactPersonsForActions = (
    Array.isArray(interpretationData.ContactPersons)
      ? interpretationData.ContactPersons
      : []
  )
    .map((contact) => ({
      id: contact?.id || contact?.ContactPersonID || null,
      Name: (contact?.Name || "").trim(),
      PhoneNumber: (contact?.PhoneNumber || "").trim(),
      Ext: String(
        contact?.Ext || contact?.ext || contact?.ContactPersonExt || "",
      ).trim(),
      Email: String(contact?.Email || "").trim(),
    }))
    .filter((contact) => contact.Name || contact.PhoneNumber || contact.Email);

  if (
    contactPersonsForActions.length === 0 &&
    interpretationData.ContactPersonName
  ) {
    contactPersonsForActions.push({
      id: null,
      Name: String(interpretationData.ContactPersonName || "").trim(),
      PhoneNumber: String(interpretationData.ContactPersonNumber || "").trim(),
      Ext: String(
        interpretationData.ContactPersonExt ||
          interpretationData.ContactPerson?.Ext ||
          interpretationData.ContactPerson?.ext ||
          "",
      ).trim(),
      Email: String(interpretationData.ContactPerson?.Email || "").trim(),
    });
  }

  const contactPersonsWithPhone = contactPersonsForActions.filter((contact) =>
    hasMeaningfulValue(contact.PhoneNumber),
  );

  const contactPersonsCardItems =
    contactPersonsForActions.length > 0
      ? contactPersonsForActions.map((contact, index) => ({
          subtitle: contact.Name || `Contact ${index + 1}`,
          content: (
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="body2">
                {`Phone: ${contact.PhoneNumber || "N/A"}`}
              </Typography>
              {hasMeaningfulValue(contact.Ext) ? (
                <Typography variant="body2">{`Ext: ${contact.Ext}`}</Typography>
              ) : null}
              {hasMeaningfulValue(contact.Email) ? (
                <Typography variant="body2">{`Email: ${contact.Email}`}</Typography>
              ) : null}
            </Box>
          ),
          actions: [
            ...(hasMeaningfulValue(contact.PhoneNumber)
              ? [
                  {
                    key: `call-${contact.id || index}`,
                    label: "Call",
                    icon: <CallRoundedIcon fontSize="small" />,
                    onClick: () =>
                      openCallLink(contact.PhoneNumber, contact.Ext),
                  },
                ]
              : []),
            ...(hasMeaningfulValue(contact.Email)
              ? [
                  {
                    key: `email-${contact.id || index}`,
                    label: "Email",
                    icon: <EmailRoundedIcon fontSize="small" />,
                    onClick: () => {
                      const subject =
                        "Regarding Assignment N° " +
                        interpretationData.ProjectNumber;
                      const mailtoLink = `mailto:${contact.Email}?subject=${encodeURIComponent(subject)}`;
                      window.open(mailtoLink, "_blank");
                    },
                  },
                ]
              : []),
          ],
        }))
      : [
          {
            subtitle: "",
            content: "No contact persons",
            centerContent: true,
          },
        ];

  // Offer summary removed: render invited linguists list instead.
  const listOfDetails = [
    {
      title: "Linguist Data",
      items: [
        { subtitle: "Language", content: targetLanguage || "N/A" },
        {
          subtitle: "Rate",
          content: formatAmount(interpretationData.Rate) || "N/A",
        },
      ],
    },
    {
      title: "Contact Persons",
      items: contactPersonsCardItems,
    },
    {
      title: "Assignment",
      items: [
        { subtitle: "Name", content: interpretationData.Name || "N/A" },
        {
          subtitle: "Type",
          content: interpretationData.ServiceSubtype?.Name || "N/A",
        },
        {
          subtitle: "Address",
          content: interpretationData.Address || "N/A",
          actions: interpretationData.Address
            ? [
                {
                  key: "maps",
                  label: "Open in Maps",
                  onClick: () => {
                    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      interpretationData.Address,
                    )}`;
                    window.open(mapsUrl, "_blank", "noopener,noreferrer");
                  },
                },
                {
                  key: "copy",
                  label: "Copy address",
                  icon: <ContentCopyRoundedIcon fontSize="small" />,
                  onClick: () => {
                    navigator.clipboard.writeText(interpretationData.Address);
                    showSnackbar(
                      "Address/Link copied to clipboard.",
                      theme,
                      "info",
                    );
                  },
                },
              ]
            : [],
          onClick: interpretationData.Address
            ? () => {
                navigator.clipboard.writeText(interpretationData.Address);
                showSnackbar(
                  "Address/Link copied to clipboard.",
                  theme,
                  "info",
                );
              }
            : null,
        },
      ],
    },
    {
      title: "Duration (Initial)",
      items: [
        {
          subtitle: "Start",
          content: formatTimeToHHMM12Hr(interpretationData.TimeStart),
        },
        {
          subtitle: "End",
          content: formatTimeToHHMM12Hr(interpretationData.TimeEnd),
        },
        {
          subtitle: "Total",
          content: formatDurationToHHMM(interpretationData.Duration),
        },
      ],
    },
    {
      title: "Duration (Actual)",
      items: [
        {
          subtitle: "Start",
          content: formatTimeToHHMM12Hr(interpretationData.ActualStart),
        },
        {
          subtitle: "End",
          content: formatTimeToHHMM12Hr(interpretationData.ActualEnd),
        },
        {
          subtitle: "Total",
          content: formatDurationToHHMM(interpretationData.ActualDuration),
        },
      ],
    },
    {
      title: "Duration (Total)",
      items: [
        {
          subtitle: "Scheduling Time",
          content: formatDurationToHHMM(interpretationData.SchedulingTime),
        },
        {
          subtitle: "Preparation Time",
          content: formatDurationToHHMM(interpretationData.PreparationTime),
        },
        {
          subtitle: "Total",
          content: formatDurationToHHMM(interpretationData.TotalDuration),
        },
      ],
    },
  ];

  // Add cancellation card if status contains "Cancelled"
  if (
    interpretationData.InterpretationStatus &&
    interpretationData.InterpretationStatus.startsWith("Cancelled")
  ) {
    listOfDetails.push({
      title: interpretationData.InterpretationStatus,
      items: [
        {
          subtitle: "Reason",
          content: interpretationData.CancellationReason || "N/A",
          multiline: true,
          centerContent: false,
        },
      ],
    });
  }

  // Reorder details so that Offer Status is at the top, followed by Client Data and Linguist Data
  const offerIndex = listOfDetails.findIndex(
    (detail) => detail.title === "Offer Status",
  );
  if (offerIndex > -1) {
    const [offerDetail] = listOfDetails.splice(offerIndex, 1);

    // Extract Client and Linguist details if present
    const clientIndex = listOfDetails.findIndex(
      (detail) => detail.title === "Client Data",
    );
    const clientDetail =
      clientIndex > -1 ? listOfDetails.splice(clientIndex, 1)[0] : null;

    // Recompute linguist index after possible client removal
    const linguistIndex = listOfDetails.findIndex(
      (detail) => detail.title === "Linguist Data",
    );
    const linguistDetail =
      linguistIndex > -1 ? listOfDetails.splice(linguistIndex, 1)[0] : null;

    // Prepend Offer, then Client, then Linguist (only if they existed)
    const toPrepend = [offerDetail];
    if (clientDetail) toPrepend.push(clientDetail);
    if (linguistDetail) toPrepend.push(linguistDetail);

    listOfDetails.unshift(...toPrepend);
  }

  const contactPersonsIndex = listOfDetails.findIndex(
    (detail) => detail.title === "Contact Persons",
  );
  if (contactPersonsIndex > -1) {
    const [contactPersonsDetail] = listOfDetails.splice(contactPersonsIndex, 1);
    listOfDetails.push(contactPersonsDetail);
  }

  const renderLegacyDetails = () => (
    <Grid container spacing={2}>
      {listOfDetails.map((detail, index) => {
        const isOffer = detail.title === "Offer Status";
        const gridSize = isOffer
          ? { xs: 12, lg: 12, xl: 12 }
          : { xs: 12, lg: 12, xl: 6 };

        return (
          <Grid key={index} size={gridSize}>
            <Card2
              title={detail.title}
              items={detail.items}
              justify="start"
              //allowShowMore={detail.allowShowMore ?? false}
            />
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Box
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, // Take full viewport height minus margin
      }}
    >
      <Box
        id="test"
        borderRadius="0 0 1.5rem 1.5rem"
        bgcolor={theme.palette.background.alt}
        p={isMobile ? "0.5rem" : "0.5rem 1rem"}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          overflowY: isMobile ? "hidden" : "hidden",
          overflowX: "hidden",
        }}
      >
        {isMobile ? (
          // ==================== MOBILE LAYOUT ====================
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
            }}
          >
            <Tabs
              value={mobileTabValue}
              onChange={handleMobileTabsChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                minHeight: "48px",
                bgcolor: theme.palette.background.alt,
                boxShadow: "0 6px 12px -10px rgba(0, 0, 0, 0.35)",
                flexShrink: 0,
              }}
            >
              <CustomTab label="Details" />
              <CustomTab label="Comments" />
            </Tabs>

            <Box
              sx={{
                flex: 1,
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minHeight: 0,
              }}
            >
              {mobileTabValue === 0 ? (
                <Box
                  ref={detailsRef}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: 0,
                  }}
                >
                  {/* 85% scrollable content */}
                  <Box
                    sx={{
                      flex: "85%",
                      overflowY: "auto",
                      scrollMarginTop: "72px",
                    }}
                  >
                    <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                      Details
                    </Typography>
                    {renderLegacyDetails()}
                  </Box>

                  {/* 15% fixed Actions section */}
                  <Box
                    ref={actionsRef}
                    sx={{
                      height: "15%",
                      minHeight: 120,
                      p: ".75rem 0",
                      borderTop: `1px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.background.alt,
                    }}
                  >
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h5" fontWeight="bold">
                        Actions
                      </Typography>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    <Box
                      width="100%"
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {/* Finalize Button */}
                      <Tooltip
                        title={
                          !canFinalizeAssignment()
                            ? "Finalize is only available after the assignment end time"
                            : "Finalize this assignment"
                        }
                        placement="top"
                      >
                        <Button
                          variant="contained"
                          color="error"
                          onClick={finalizeAssignment}
                          disabled={!canFinalizeAssignment()}
                          sx={{ minWidth: 120 }}
                        >
                          Finalize
                        </Button>
                      </Tooltip>

                      {/* Call Button with Menu */}
                      <Menu
                        id="call-menu"
                        anchorEl={callMenuAnchor}
                        open={callMenuOpen}
                        onClose={() => setCallMenuOpen(false)}
                        MenuListProps={{
                          "aria-labelledby": "call-button",
                        }}
                      >
                        {contactPersonsWithPhone.map((contact, index) => (
                          <MenuItem
                            key={`call-menu-item-${contact.id || contact.Name || index}`}
                            onClick={() => {
                              openCallLink(contact.PhoneNumber, contact.Ext);
                              setCallMenuOpen(false);
                            }}
                          >
                            <ListItemIcon>
                              <CallRoundedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Call ${contact.Name || "Contact"}`}
                            />
                          </MenuItem>
                        ))}
                        {contactPersonsWithPhone.length === 0 && (
                          <MenuItem disabled>
                            <ListItemText primary="No contacts available" />
                          </MenuItem>
                        )}
                      </Menu>
                      <Button
                        id="call-button"
                        variant="outlined"
                        startIcon={<CallRoundedIcon />}
                        onClick={(e) => setCallMenuAnchor(e.currentTarget)}
                        disabled={contactPersonsWithPhone.length === 0}
                        sx={{ minWidth: 100 }}
                      >
                        Call
                      </Button>

                      {/* Address Button with Menu */}
                      <Menu
                        id="address-menu"
                        anchorEl={addressMenuAnchor}
                        open={addressMenuOpen}
                        onClose={() => setAddressMenuOpen(false)}
                        anchorReference="anchorPosition"
                        anchorPosition={{
                          top:
                            addressMenuAnchor?.getBoundingClientRect().top + 12,
                          left: addressMenuAnchor?.getBoundingClientRect().left,
                        }}
                        slotProps={{
                          paper: {
                            sx: {
                              bgcolor: theme.palette.background.alt,
                              border: `1px solid ${theme.palette.divider}`,
                              "& .MuiListItemIcon-root": {
                                color: theme.palette.primary.main,
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            navigator.clipboard.writeText(
                              interpretationData.Address,
                            );
                            showSnackbar(
                              "Address copied to clipboard.",
                              theme,
                              "info",
                            );
                            setAddressMenuOpen(false);
                          }}
                        >
                          <ListItemIcon>
                            <ContentCopyRoundedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ color: theme.palette.primary.text }}
                          >
                            Copy Address
                          </ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              interpretationData.Address,
                            )}`;
                            window.open(
                              mapsUrl,
                              "_blank",
                              "noopener,noreferrer",
                            );
                            setAddressMenuOpen(false);
                          }}
                        >
                          <ListItemIcon>
                            <MapRoundedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ color: theme.palette.primary.text }}
                          >
                            Open in Maps
                          </ListItemText>
                        </MenuItem>
                      </Menu>

                      <Button
                        id="address-button"
                        variant="outlined"
                        startIcon={<LocationOnRoundedIcon />}
                        onClick={(e) => {
                          e.preventDefault();
                          setAddressMenuAnchor(e.currentTarget);
                          setAddressMenuOpen(true);
                        }}
                        disabled={!interpretationData.Address}
                        sx={{ minWidth: 120 }}
                      >
                        Address
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  ref={commentsRef}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: 0,
                    overflow: "hidden",
                    scrollMarginTop: "72px",
                  }}
                >
                  {/* Messages area - scrollable */}
                  <Box
                    ref={commentsListRef}
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {comments && comments.length > 0 ? (
                      comments.map((serviceComment, index) => {
                        const commentKey =
                          serviceComment.id ||
                          `${serviceComment.createdOn}-${index}`;
                        const isMine = serviceComment.authorType === "linguist";

                        return (
                          <Box
                            key={commentKey}
                            sx={{
                              display: "flex",
                              justifyContent: isMine
                                ? "flex-end"
                                : "flex-start",
                            }}
                          >
                            <Paper
                              variant="outlined"
                              //onContextMenu={(event) =>
                              //  handleContextMenu(event, serviceComment)
                              //}
                              sx={{
                                width: "100%",
                                maxWidth: "78%",
                                p: 2,
                                borderRadius: 2,
                                bgcolor: isMine
                                  ? theme.palette.primary.main
                                  : theme.palette.background.default,
                                color: isMine
                                  ? theme.palette.background.default
                                  : theme.palette.primary.text,
                                boxShadow: 1,
                                cursor: "pointer",
                              }}
                            >
                              <Stack spacing={1.25}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: 2,
                                  }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 700,
                                        color: "inherit",
                                      }}
                                    >
                                      {serviceComment.author}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="inherit"
                                    >
                                      {serviceComment.createdLabel}
                                    </Typography>
                                  </Box>
                                  {serviceComment.hasEditHistory ? (
                                    <Chip
                                      size="small"
                                      label="Edited"
                                      variant="outlined"
                                    />
                                  ) : null}
                                </Box>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    color: "inherit",
                                  }}
                                >
                                  {serviceComment.note}
                                </Typography>

                                {serviceComment.historyEntries?.length > 0 ? (
                                  <Box
                                    sx={{
                                      mt: 0.5,
                                      p: 1.25,
                                      borderRadius: 1.5,
                                      bgcolor: theme.palette.action.hover,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 700,
                                        display: "block",
                                        mb: 0.75,
                                      }}
                                    >
                                      Edit history
                                    </Typography>
                                    <Stack spacing={0.5}>
                                      {serviceComment.historyEntries?.map(
                                        (historyEntry) => (
                                          <Box key={historyEntry.id}>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ display: "block" }}
                                            >
                                              {historyEntry.label}
                                              {historyEntry.changedBy
                                                ? ` · ${historyEntry.changedBy}`
                                                : ""}
                                              {historyEntry.changedOn
                                                ? ` · ${formatServiceCommentTime(historyEntry.changedOn)}`
                                                : ""}
                                            </Typography>
                                            {historyEntry.noteFrom ||
                                            historyEntry.noteTo ? (
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                              >
                                                {historyEntry.noteFrom ||
                                                  "(previous)"}{" "}
                                                →{" "}
                                                {historyEntry.noteTo ||
                                                  "(current)"}
                                              </Typography>
                                            ) : null}
                                          </Box>
                                        ),
                                      )}
                                    </Stack>
                                  </Box>
                                ) : null}
                              </Stack>
                            </Paper>
                          </Box>
                        );
                      })
                    ) : (
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: 160,
                        }}
                      >
                        <Typography color="text.secondary">
                          No comments yet.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Input area - fixed at bottom of this section */}
                  <Box
                    sx={{
                      p: 0.5,
                      pt: 1,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      background: theme.palette.background.alt,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={1}
                        maxRows={6}
                        placeholder="Write a message..."
                        value={newCommentText}
                        onChange={(e) => setNewCommentText(e.target.value)}
                      />
                      <Button
                        variant="contained"
                        onClick={addComment}
                        startIcon={<SendRoundedIcon />}
                        disabled={!String(newCommentText || "").trim()}
                      >
                        Send
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          // ==================== DESKTOP LAYOUT ====================
          <Grid
            container
            spacing={2}
            sx={{
              flex: 1,
              width: "100%",
              height: "100%",
              minHeight: 0,
            }}
          >
            {/* MIDDLE COLUMN - Details Tab */}
            <Grid
              size={{ xs: 12, md: "grow" }}
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                flex: "1 1 auto",
                height: "100%",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabsChange}
                    aria-label="basic tabs example"
                    sx={{ bgcolor: theme.palette.background.alt }}
                  >
                    <CustomTab label="Details" {...a11yProps(0)} />
                    <CustomTab label="Comments" {...a11yProps(1)} />
                  </Tabs>
                </Box>

                <CustomTabPanel
                  value={tabValue}
                  index={0}
                  key="Tab One"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* 85% scrollable content */}
                  <Box
                    sx={{
                      flex: "85%",
                      overflowY: "auto",
                      p: ".75rem 0.25rem",
                    }}
                  >
                    {/* General Information moved here so all details are in one tab */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="h4" fontWeight="bold">
                          General Information
                        </Typography>
                      </Grid>
                      <Grid container size={{ xs: 12 }} spacing={1}>
                        <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                          <Card
                            big
                            subtitle="Number"
                            content={interpretationData.ProjectNumber || "N/A"}
                            theme={theme}
                            onClick={() => {
                              navigator.clipboard.writeText(
                                interpretationData.ProjectNumber,
                              );
                              showSnackbar(
                                "Project number copied to clipboard.",
                                theme,
                                "info",
                              );
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                          <Card
                            big
                            subtitle="Status"
                            content={
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  px: 1.25,
                                  py: 0.25,
                                  borderRadius: "8px",
                                  backgroundColor: getStatusColor(
                                    theme,
                                    interpretationData.InterpretationStatus ||
                                      "N/A",
                                  ),
                                  color: theme.palette.background.default,
                                  fontSize: "inherit",
                                  fontWeight: 700,
                                  lineHeight: 1.2,
                                  textAlign: "center",
                                }}
                              >
                                {displayStatus || "N/A"}
                              </Box>
                            }
                            theme={theme}
                          />
                        </Grid>
                        <Grid
                          size={{ xs: 12 }}
                          container
                          spacing={1}
                          p=".25rem"
                        >
                          <Card
                            big
                            subtitle="Date"
                            content={formatDateLong(interpretationData.Date)}
                            theme={theme}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                          <Card
                            subtitle="Total Linguist Pay"
                            content={formatCurrencyValue(computedTotal)}
                            theme={theme}
                          />
                        </Grid>
                        {visibleCostLines.length === 0 ? (
                          <Grid
                            size={{ xs: 6 }}
                            container
                            spacing={1}
                            p=".25rem"
                          >
                            <Card
                              subtitle="Cost Breakdown"
                              content={"No costs"}
                              theme={theme}
                            />
                          </Grid>
                        ) : (
                          visibleCostLines.map((cl, idx) => (
                            <Grid
                              key={`cost-card-${cl.id || idx}`}
                              size={{ xs: 6 }}
                              container
                              spacing={1}
                              p=".25rem"
                            >
                              <Card
                                subtitle={
                                  cl.CustomLabel ||
                                  cl.CostType?.Name ||
                                  cl.SourceLegacyField ||
                                  "Cost"
                                }
                                content={formatCurrencyValue(cl.Amount)}
                                theme={theme}
                              />
                            </Grid>
                          ))
                        )}
                      </Grid>
                    </Grid>

                    {renderLegacyDetails()}
                  </Box>

                  {/* 15% fixed Actions section */}
                  <Box
                    sx={{
                      height: "15%",
                      minHeight: 120,
                      p: ".75rem 0",
                      borderTop: `1px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.background.alt,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Divider sx={{ my: 2 }} />
                    
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h5" fontWeight="bold">
                        Actions
                      </Typography>
                    </Grid>

                    
                    <Box
                      width="100%"
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {/* Finalize Button */}
                      <Tooltip
                        title={
                          !canFinalizeAssignment()
                            ? "Finalize is only available after the assignment end time"
                            : "Finalize this assignment"
                        }
                        placement="top"
                      >
                        <Button
                          variant="contained"
                          color="error"
                          onClick={finalizeAssignment}
                          disabled={!canFinalizeAssignment()}
                          sx={{ minWidth: 120 }}
                        >
                          Finalize
                        </Button>
                      </Tooltip>

                      {/* Call Button with Menu */}
                      <Menu
                        id="call-menu"
                        anchorEl={callMenuAnchor}
                        open={callMenuOpen}
                        onClose={() => setCallMenuOpen(false)}
                        MenuListProps={{
                          "aria-labelledby": "call-button",
                        }}
                      >
                        {contactPersonsWithPhone.map((contact, index) => (
                          <MenuItem
                            key={`call-menu-item-${contact.id || contact.Name || index}`}
                            onClick={() => {
                              openCallLink(contact.PhoneNumber, contact.Ext);
                              setCallMenuOpen(false);
                            }}
                          >
                            <ListItemIcon>
                              <CallRoundedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Call ${contact.Name || "Contact"}`}
                            />
                          </MenuItem>
                        ))}
                        {contactPersonsWithPhone.length === 0 && (
                          <MenuItem disabled>
                            <ListItemText primary="No contacts available" />
                          </MenuItem>
                        )}
                      </Menu>
                      <Button
                        id="call-button"
                        variant="outlined"
                        startIcon={<CallRoundedIcon />}
                        onClick={(e) => setCallMenuAnchor(e.currentTarget)}
                        disabled={contactPersonsWithPhone.length === 0}
                        sx={{ minWidth: 100 }}
                      >
                        Call
                      </Button>

                      {/* Address Button with Menu */}
                      <Menu
                        id="address-menu"
                        anchorEl={addressMenuAnchor}
                        open={addressMenuOpen}
                        onClose={() => setAddressMenuOpen(false)}
                        anchorReference="anchorPosition"
                        anchorPosition={{
                          top:
                            addressMenuAnchor?.getBoundingClientRect().top + 12,
                          left: addressMenuAnchor?.getBoundingClientRect().left,
                        }}
                        slotProps={{
                          paper: {
                            sx: {
                              bgcolor: theme.palette.background.alt,
                              border: `1px solid ${theme.palette.divider}`,
                              "& .MuiListItemIcon-root": {
                                color: theme.palette.primary.main,
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            navigator.clipboard.writeText(
                              interpretationData.Address,
                            );
                            showSnackbar(
                              "Address copied to clipboard.",
                              theme,
                              "info",
                            );
                            setAddressMenuOpen(false);
                          }}
                        >
                          <ListItemIcon>
                            <ContentCopyRoundedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ color: theme.palette.primary.text }}
                          >
                            Copy Address
                          </ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              interpretationData.Address,
                            )}`;
                            window.open(
                              mapsUrl,
                              "_blank",
                              "noopener,noreferrer",
                            );
                            setAddressMenuOpen(false);
                          }}
                        >
                          <ListItemIcon>
                            <MapRoundedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            sx={{ color: theme.palette.primary.text }}
                          >
                            Open in Maps
                          </ListItemText>
                        </MenuItem>
                      </Menu>
                      
                      <Button
                        id="address-button"
                        variant="outlined"
                        startIcon={<LocationOnRoundedIcon />}
                        onClick={(e) => {
                          e.preventDefault();
                          setAddressMenuAnchor(e.currentTarget);
                          setAddressMenuOpen(true);
                        }}
                        disabled={!interpretationData.Address}
                        sx={{ minWidth: 120 }}
                      >
                        Address
                      </Button>
                    </Box>
                  </Box>
                </CustomTabPanel>

                <CustomTabPanel
                  value={tabValue}
                  index={1}
                  key="Tab Comments"
                  sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <Box
                    sx={{
                      p: ".75rem 0.25rem",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      minHeight: 0,
                    }}
                  >
                    <Box
                      ref={commentsListRef}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {comments && comments.length > 0 ? (
                        comments.map((serviceComment, index) => {
                          const commentKey =
                            serviceComment.id ||
                            `${serviceComment.createdOn}-${index}`;
                          const isMine =
                            serviceComment.authorType === "linguist";

                          return (
                            <Box
                              key={commentKey}
                              sx={{
                                display: "flex",
                                justifyContent: isMine
                                  ? "flex-end"
                                  : "flex-start",
                              }}
                            >
                              <Paper
                                variant="outlined"
                                //onContextMenu={(event) =>
                                //  handleContextMenu(event, serviceComment)
                                //}
                                sx={{
                                  width: "100%",
                                  maxWidth: "78%",
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: isMine
                                    ? theme.palette.primary.main
                                    : theme.palette.background.default,
                                  color: isMine
                                    ? theme.palette.background.default
                                    : theme.palette.primary.text,
                                  boxShadow: 1,
                                  cursor: "pointer",
                                }}
                              >
                                <Stack spacing={1.25}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: 2,
                                    }}
                                  >
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 700,
                                          color: "inherit",
                                        }}
                                      >
                                        {serviceComment.author}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="inherit"
                                      >
                                        {serviceComment.createdLabel}
                                      </Typography>
                                    </Box>
                                    {serviceComment.hasEditHistory ? (
                                      <Chip
                                        size="small"
                                        label="Edited"
                                        variant="outlined"
                                      />
                                    ) : null}
                                  </Box>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                      color: "inherit",
                                    }}
                                  >
                                    {serviceComment.note}
                                  </Typography>

                                  {serviceComment.historyEntries?.length > 0 ? (
                                    <Box
                                      sx={{
                                        mt: 0.5,
                                        p: 1.25,
                                        borderRadius: 1.5,
                                        bgcolor: theme.palette.action.hover,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 700,
                                          display: "block",
                                          mb: 0.75,
                                        }}
                                      >
                                        Edit history
                                      </Typography>
                                      <Stack spacing={0.5}>
                                        {serviceComment.historyEntries?.map(
                                          (historyEntry) => (
                                            <Box key={historyEntry.id}>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block" }}
                                              >
                                                {historyEntry.label}
                                                {historyEntry.changedBy
                                                  ? ` · ${historyEntry.changedBy}`
                                                  : ""}
                                                {historyEntry.changedOn
                                                  ? ` · ${formatServiceCommentTime(historyEntry.changedOn)}`
                                                  : ""}
                                              </Typography>
                                              {historyEntry.noteFrom ||
                                              historyEntry.noteTo ? (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  {historyEntry.noteFrom ||
                                                    "(previous)"}{" "}
                                                  →{" "}
                                                  {historyEntry.noteTo ||
                                                    "(current)"}
                                                </Typography>
                                              ) : null}
                                            </Box>
                                          ),
                                        )}
                                      </Stack>
                                    </Box>
                                  ) : null}
                                </Stack>
                              </Paper>
                            </Box>
                          );
                        })
                      ) : (
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 160,
                          }}
                        >
                          <Typography color="text.secondary">
                            No comments yet.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={1}
                          maxRows={6}
                          placeholder="Write a message..."
                          value={newCommentText}
                          onChange={(e) => setNewCommentText(e.target.value)}
                        />
                        <Button
                          variant="contained"
                          onClick={addComment}
                          startIcon={<SendRoundedIcon />}
                          disabled={!String(newCommentText || "").trim()}
                        >
                          Send
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </CustomTabPanel>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default LinguistAssignmentDetail;
